import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ShareDialog } from "@/components/ShareDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Plus,
  Upload,
  Trash2,
  Loader2,
  Eye,
  Layers,
  RefreshCw,
  Check,
  CheckCircle2,
  AlertCircle,
  Clock,
  Share2,
  FileText,
  Sparkles,
  Image as ImageIcon,
  LayoutTemplate,
  Search,
  Grid3X3,
  List,
  MoreVertical,
  Edit3,
  Copy,
  Download,
  Wand2,
  Palette,
  User,
  Users,
  Type,
  ScanSearch,
  Image,
  MapPin,
  X,
  Replace
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom component for authenticated image loading
const AuthenticatedImage = ({ src, alt, className, fallback }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadImage = async () => {
      try {
        const response = await axios.get(src, { responseType: 'blob' });
        if (isMounted) {
          const blobUrl = URL.createObjectURL(response.data);
          setImageUrl(blobUrl);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };
    
    loadImage();
    
    return () => {
      isMounted = false;
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [src]);

  if (loading) return <Skeleton className={className} />;
  if (error || !imageUrl) return fallback || null;
  
  return <img src={imageUrl} alt={alt} className={className} />;
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: "Ausstehend", variant: "secondary", icon: Clock },
    analyzing: { label: "Analysiert...", variant: "default", icon: Loader2, animate: true },
    completed: { label: "Bereit", variant: "success", icon: CheckCircle2 },
    failed: { label: "Fehler", variant: "destructive", icon: AlertCircle }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge 
      variant={config.variant === "success" ? "default" : config.variant}
      className={`gap-1 ${config.variant === "success" ? "bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30" : ""}`}
    >
      <Icon className={`h-3 w-3 ${config.animate ? "animate-spin" : ""}`} />
      {config.label}
    </Badge>
  );
};

// Template card component
const TemplateCard = ({ template, onView, onDelete, onShare, onDuplicate, onGenerate }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);

  useEffect(() => {
    // Load the original image thumbnail via axios to include auth header
    const loadImage = async () => {
      if (template.has_original_image && template.status === "completed") {
        try {
          const response = await axios.get(
            `${API}/meta-ad-templates/${template.id}/image`,
            { responseType: 'blob' }
          );
          const blobUrl = URL.createObjectURL(response.data);
          setImageUrl(blobUrl);
        } catch (err) {
          console.error("Error loading template image:", err);
          setImageUrl(null);
        }
      }
      setLoadingImage(false);
    };
    
    loadImage();
    
    // Cleanup blob URL on unmount
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [template.id, template.has_original_image, template.status]);

  const elementCount = template.blueprint?.elements?.length || 0;
  const createdDate = new Date(template.created_at).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });

  return (
    <Card 
      className="group relative overflow-hidden hover:shadow-lg transition-all duration-200 border-border/50 hover:border-primary/30"
      data-testid={`template-card-${template.id}`}
    >
      {/* Image Preview */}
      <div className="aspect-square relative bg-muted/30 overflow-hidden">
        {loadingImage ? (
          <Skeleton className="absolute inset-0" />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={template.name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImageUrl(null)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <LayoutTemplate className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onView(template)}
            data-testid={`view-template-${template.id}`}
          >
            <Eye className="h-4 w-4 mr-1" />
            Ansehen
          </Button>
        </div>

        {/* Status badge */}
        <div className="absolute top-2 left-2">
          <StatusBadge status={template.status} />
        </div>

        {/* Actions dropdown */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="secondary" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(template)}>
                <Eye className="h-4 w-4 mr-2" />
                Details anzeigen
              </DropdownMenuItem>
              {template.status === "completed" && (
                <DropdownMenuItem onClick={() => onGenerate(template)}>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Bild generieren
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onShare(template)}>
                <Share2 className="h-4 w-4 mr-2" />
                Teilen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(template)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplizieren
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => onDelete(template)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Card Content */}
      <CardContent className="p-4">
        <h3 className="font-semibold text-sm truncate mb-1" title={template.name}>
          {template.name}
        </h3>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{createdDate}</span>
          {template.status === "completed" && (
            <span className="flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {elementCount} Elemente
            </span>
          )}
        </div>
        {template.error_message && (
          <p className="text-xs text-destructive mt-2 truncate" title={template.error_message}>
            {template.error_message}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Upload dialog component
const UploadDialog = ({ open, onOpenChange, onUpload }) => {
  const [name, setName] = useState("");
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte nur Bilddateien hochladen");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Bild zu groß (max. 10 MB)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setImageData(event.target.result);
      setImagePreview(event.target.result);
      // Auto-fill name from filename if empty
      if (!name) {
        const fileName = file.name.replace(/\.[^/.]+$/, "");
        setName(fileName);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Bitte einen Namen eingeben");
      return;
    }
    if (!imageData) {
      toast.error("Bitte ein Bild hochladen");
      return;
    }

    setUploading(true);
    try {
      await onUpload(name, imageData);
      setName("");
      setImageData(null);
      setImagePreview(null);
      onOpenChange(false);
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setImageData(null);
    setImagePreview(null);
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Neues Template erstellen
          </DialogTitle>
          <DialogDescription>
            Lade ein Meta Ad Bild hoch. GPT-4o Vision analysiert das Layout und extrahiert ein wiederverwendbares Blueprint.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name input */}
          <div className="space-y-2">
            <Label htmlFor="template-name">Template-Name</Label>
            <Input
              id="template-name"
              placeholder="z.B. 'Recruiting Ad Blue'"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="template-name-input"
            />
          </div>

          {/* Image upload */}
          <div className="space-y-2">
            <Label>Werbebild</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive 
                  ? "border-primary bg-primary/5" 
                  : "border-muted-foreground/25 hover:border-muted-foreground/50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {imagePreview ? (
                <div className="space-y-3">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded-lg shadow-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setImageData(null); setImagePreview(null); }}
                  >
                    Anderes Bild wählen
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      Bild hierher ziehen oder{" "}
                      <label className="text-primary cursor-pointer hover:underline">
                        durchsuchen
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                          data-testid="template-image-input"
                        />
                      </label>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PNG, JPG bis 10 MB
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Info box */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
            <p className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
              <span>
                Die KI-Analyse dauert ca. 30-60 Sekunden. Dabei werden Texte, Farben, Layout und Schriftarten erkannt.
              </span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={uploading || !name.trim() || !imageData}>
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wird analysiert...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Template erstellen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Template detail dialog
const TemplateDetailDialog = ({ template, open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState("preview");

  if (!template) return null;

  const blueprint = template.blueprint || {};
  const elements = blueprint.elements || [];
  const typography = blueprint.typography?.scale || {};
  const background = blueprint.background?.layers || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutTemplate className="h-5 w-5 text-primary" />
            {template.name}
          </DialogTitle>
          <DialogDescription>
            Template-Details und Blueprint-Struktur
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="preview" className="gap-2">
              <ImageIcon className="h-4 w-4" />
              Vorschau
            </TabsTrigger>
            <TabsTrigger value="elements" className="gap-2">
              <Layers className="h-4 w-4" />
              Elemente ({elements.length})
            </TabsTrigger>
            <TabsTrigger value="styles" className="gap-2">
              <FileText className="h-4 w-4" />
              Styles
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[500px] mt-4">
            <TabsContent value="preview" className="mt-0">
              <div className="flex justify-center p-4 bg-muted/30 rounded-lg">
                {template.has_original_image ? (
                  <AuthenticatedImage
                    src={`${API}/meta-ad-templates/${template.id}/image`}
                    alt={template.name}
                    className="max-h-[400px] rounded-lg shadow-lg"
                    fallback={
                      <div className="flex items-center justify-center h-64 text-muted-foreground">
                        <LayoutTemplate className="h-16 w-16" />
                      </div>
                    }
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-muted-foreground">
                    <LayoutTemplate className="h-16 w-16" />
                  </div>
                )}
              </div>
              
              {/* Canvas info */}
              {blueprint.canvas && (
                <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-medium mb-2">Canvas</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Breite:</span>{" "}
                      <span className="font-medium">{blueprint.canvas.width_px}px</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Höhe:</span>{" "}
                      <span className="font-medium">{blueprint.canvas.height_px}px</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Safe Margin:</span>{" "}
                      <span className="font-medium">{blueprint.canvas.safe_margin_px}px</span>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="elements" className="mt-0 space-y-3">
              {elements.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Keine Elemente gefunden</p>
                </div>
              ) : (
                elements.map((element, idx) => (
                  <Card key={element.id || idx} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">
                            {element.type}
                          </Badge>
                          {element.editable && (
                            <Badge variant="secondary" className="text-xs">
                              <Edit3 className="h-3 w-3 mr-1" />
                              Editierbar
                            </Badge>
                          )}
                        </div>
                        {element.text && (
                          <p className="text-sm font-medium mt-2">"{element.text}"</p>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          Position: {element.placement?.x_pct}% / {element.placement?.y_pct}% • 
                          Größe: {element.placement?.w_pct}% × {element.placement?.h_pct}%
                        </div>
                      </div>
                      {element.style?.fill_color_hex && (
                        <div 
                          className="w-8 h-8 rounded border"
                          style={{ backgroundColor: element.style.fill_color_hex }}
                          title={element.style.fill_color_hex}
                        />
                      )}
                    </div>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="styles" className="mt-0 space-y-4">
              {/* Typography */}
              <Card className="p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Typografie
                </h4>
                {Object.keys(typography).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Keine Typografie-Definitionen</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(typography).map(([name, style]) => (
                      <div key={name} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                        <span className="text-sm font-medium">{name}</span>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{style.size_px}px</span>
                          <span>Weight: {style.weight}</span>
                          {style.color_hex && (
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: style.color_hex }}
                              title={style.color_hex}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Background */}
              <Card className="p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Hintergrund
                </h4>
                {background.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Keine Hintergrund-Layer</p>
                ) : (
                  <div className="space-y-2">
                    {background.map((layer, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                        <div className="text-sm">
                          <span className="font-medium capitalize">{layer.type}</span>
                          {layer.content_description && (
                            <span className="text-muted-foreground ml-2">
                              - {layer.content_description}
                            </span>
                          )}
                        </div>
                        {layer.style?.fill?.color_hex && (
                          <div 
                            className="w-6 h-6 rounded border"
                            style={{ backgroundColor: layer.style.fill.color_hex }}
                            title={layer.style.fill.color_hex}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Export Hints */}
              {blueprint.export_hints?.platform_presets && (
                <Card className="p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Export-Formate
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {blueprint.export_hints.platform_presets.map((preset, idx) => (
                      <Badge key={idx} variant="outline">
                        {preset.name}: {preset.width_px}×{preset.height_px}
                      </Badge>
                    ))}
                  </div>
                </Card>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Color swatch editor component
const ColorSwatchEditor = ({ label, colorHex, onChange, description }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempColor, setTempColor] = useState(colorHex);
  
  const handleColorChange = (e) => {
    let value = e.target.value.toUpperCase();
    // Ensure it starts with #
    if (!value.startsWith('#')) {
      value = '#' + value;
    }
    // Limit to 7 characters (#RRGGBB)
    value = value.slice(0, 7);
    setTempColor(value);
    
    // Only call onChange if it's a valid hex color
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      onChange(value);
    }
  };
  
  const isValidHex = /^#[0-9A-F]{6}$/i.test(tempColor);
  
  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
      {/* Color preview */}
      <div 
        className="relative group cursor-pointer"
        onClick={() => setIsEditing(!isEditing)}
      >
        <div 
          className="w-10 h-10 rounded-lg border-2 border-border shadow-sm transition-transform group-hover:scale-105"
          style={{ backgroundColor: isValidHex ? tempColor : colorHex }}
          data-testid={`color-swatch-${label}`}
        />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Edit3 className="h-4 w-4 text-white drop-shadow-lg" />
        </div>
      </div>
      
      {/* Label and input */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{label}</span>
          {description && (
            <span className="text-xs text-muted-foreground hidden sm:inline">
              ({description})
            </span>
          )}
        </div>
        
        {isEditing ? (
          <div className="flex items-center gap-2 mt-1">
            <Input
              type="text"
              value={tempColor}
              onChange={handleColorChange}
              className={`h-7 w-28 font-mono text-xs ${!isValidHex ? 'border-destructive' : ''}`}
              placeholder="#FFFFFF"
              data-testid={`color-input-${label}`}
            />
            <input
              type="color"
              value={isValidHex ? tempColor : colorHex}
              onChange={(e) => {
                const newColor = e.target.value.toUpperCase();
                setTempColor(newColor);
                onChange(newColor);
              }}
              className="w-7 h-7 rounded cursor-pointer border-0 p-0"
              data-testid={`color-picker-${label}`}
            />
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 px-2"
              onClick={() => setIsEditing(false)}
            >
              <CheckCircle2 className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground font-mono">{colorHex}</span>
        )}
      </div>
    </div>
  );
};

// Helper function to extract colors from blueprint
const extractColorsFromBlueprint = (blueprint) => {
  const colors = {
    background: [],
    typography: [],
    elements: []
  };
  
  if (!blueprint) return colors;
  
  // Extract background colors
  const bgLayers = blueprint.background?.layers || [];
  bgLayers.forEach((layer, idx) => {
    const fill = layer.style?.fill;
    if (fill?.color_hex) {
      colors.background.push({
        id: `bg_layer_${idx}`,
        label: `Hintergrund ${idx + 1}`,
        hex: fill.color_hex,
        description: layer.content_description || layer.type
      });
    }
    // Also check gradient stops
    if (fill?.stops) {
      fill.stops.forEach((stop, sIdx) => {
        if (stop.color_hex) {
          colors.background.push({
            id: `bg_layer_${idx}_stop_${sIdx}`,
            label: `Gradient ${idx + 1}.${sIdx + 1}`,
            hex: stop.color_hex,
            description: `Position ${stop.position}%`
          });
        }
      });
    }
  });
  
  // Extract typography colors
  const scale = blueprint.typography?.scale || {};
  Object.entries(scale).forEach(([name, style]) => {
    if (style.color_hex) {
      colors.typography.push({
        id: `typo_${name}`,
        label: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        hex: style.color_hex,
        description: `${style.size_px}px, ${style.weight}`
      });
    }
  });
  
  // Extract element colors
  const elements = blueprint.elements || [];
  elements.forEach((el) => {
    if (el.style?.fill_color_hex) {
      colors.elements.push({
        id: `el_${el.id}`,
        label: el.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        hex: el.style.fill_color_hex,
        description: el.type
      });
    }
  });
  
  return colors;
};

// Generate dialog component
const GenerateDialog = ({ template, open, onOpenChange }) => {
  const [textOverrides, setTextOverrides] = useState({});
  const [colorOverrides, setColorOverrides] = useState({});
  const [exportFormat, setExportFormat] = useState("1080x1080");
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [activeTab, setActiveTab] = useState("text");
  const [fullTemplate, setFullTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Persons selection for AI background
  const [persons, setPersons] = useState([]);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [useAiBackground, setUseAiBackground] = useState(true);

  // Load full template with blueprint when dialog opens
  useEffect(() => {
    const loadFullTemplate = async () => {
      if (open && template?.id && !fullTemplate) {
        setLoadingTemplate(true);
        try {
          const response = await axios.get(`${API}/meta-ad-templates/${template.id}`);
          setFullTemplate(response.data);
        } catch (err) {
          console.error("Error loading template:", err);
          toast.error("Fehler beim Laden des Templates");
        } finally {
          setLoadingTemplate(false);
        }
      }
    };
    loadFullTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id]);
  
  // Load persons when dialog opens
  useEffect(() => {
    const loadPersons = async () => {
      if (open && persons.length === 0) {
        setLoadingPersons(true);
        try {
          const response = await axios.get(`${API}/persons`);
          setPersons(response.data || []);
        } catch (err) {
          console.error("Error loading persons:", err);
        } finally {
          setLoadingPersons(false);
        }
      }
    };
    loadPersons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  
  // Reset fullTemplate when dialog closes
  useEffect(() => {
    if (!open) {
      setFullTemplate(null);
      setSelectedPersons([]);
    }
  }, [open]);

  // Use fullTemplate if available, otherwise fall back to template prop
  const activeTemplate = fullTemplate || template;
  
  const elements = activeTemplate?.blueprint?.elements || [];
  const editableElements = elements.filter(el => el.editable !== false && el.text);
  
  // Check if blueprint has photo layers (needs AI background)
  const hasPhotoLayers = React.useMemo(() => {
    const layers = activeTemplate?.blueprint?.background?.layers || [];
    return layers.some(l => l.type === "photo");
  }, [activeTemplate?.blueprint]);
  
  // Extract colors from blueprint
  const extractedColors = React.useMemo(() => {
    return extractColorsFromBlueprint(activeTemplate?.blueprint);
  }, [activeTemplate?.blueprint]);
  
  const hasColors = extractedColors.background.length > 0 || 
                    extractedColors.typography.length > 0 || 
                    extractedColors.elements.length > 0;

  const handleTextChange = (elementId, value) => {
    setTextOverrides(prev => ({
      ...prev,
      [elementId]: value
    }));
  };
  
  const handleColorChange = (colorId, newHex) => {
    setColorOverrides(prev => ({
      ...prev,
      [colorId]: newHex
    }));
  };
  
  const handlePersonToggle = (personId) => {
    setSelectedPersons(prev => {
      if (prev.includes(personId)) {
        return prev.filter(id => id !== personId);
      } else {
        return [...prev, personId];
      }
    });
  };

  const handleGenerate = async () => {
    if (!activeTemplate?.id) return;
    
    setGenerating(true);
    setGeneratedImage(null);
    
    try {
      const response = await axios.post(`${API}/meta-ad-templates/${activeTemplate.id}/render`, {
        text_overrides: textOverrides,
        color_overrides: colorOverrides,
        export_format: exportFormat,
        selected_persons: selectedPersons,
        use_ai_background: useAiBackground && hasPhotoLayers
      });
      
      if (response.data.success) {
        setGeneratedImage(response.data.image);
        const aiUsed = response.data.ai_background_used;
        toast.success(aiUsed ? "Bild mit KI-Hintergrund generiert!" : "Bild erfolgreich generiert!");
      } else {
        toast.error("Generierung fehlgeschlagen");
      }
    } catch (err) {
      console.error("Generate error:", err);
      toast.error("Fehler bei der Generierung", {
        description: err.response?.data?.detail || "Bitte versuche es erneut."
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${generatedImage}`;
    link.download = `${activeTemplate?.name || "meta-ad"}-${exportFormat}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Bild heruntergeladen!");
  };

  const handleReset = () => {
    setTextOverrides({});
    setColorOverrides({});
    setGeneratedImage(null);
    setActiveTab("text");
    setFullTemplate(null);
  };

  if (!template) return null;

  const colorOverrideCount = Object.keys(colorOverrides).length;
  const textOverrideCount = Object.keys(textOverrides).filter(k => textOverrides[k]).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleReset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            {loadingTemplate ? "Lade Template..." : "Neues Bild generieren"}
          </DialogTitle>
          <DialogDescription>
            Passe Texte und Farben an und generiere ein neues Werbebild basierend auf dem Template "{activeTemplate?.name || template.name}".
          </DialogDescription>
        </DialogHeader>

        {loadingTemplate ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Lade Blueprint...</span>
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
          {/* Left side: Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Export-Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="export-format-select">
                  <SelectValue placeholder="Format wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1080x1080">Instagram Feed (1080×1080)</SelectItem>
                  <SelectItem value="1080x1920">Instagram Story (1080×1920)</SelectItem>
                  <SelectItem value="1200x628">Facebook Feed (1200×628)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* AI Background Toggle */}
            {hasPhotoLayers && (
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <div>
                    <Label className="text-sm font-medium">KI-Hintergrund</Label>
                    <p className="text-xs text-muted-foreground">Mit Gemini Nano Banana Pro generieren</p>
                  </div>
                </div>
                <Switch 
                  checked={useAiBackground} 
                  onCheckedChange={setUseAiBackground}
                  data-testid="ai-background-toggle"
                />
              </div>
            )}

            {/* Tabs for Text, Colors, and Persons */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={`grid w-full ${hasPhotoLayers && useAiBackground ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <TabsTrigger value="text" className="gap-2" data-testid="text-tab">
                  <Edit3 className="h-4 w-4" />
                  Texte
                  {textOverrideCount > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {textOverrideCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="colors" className="gap-2" data-testid="colors-tab">
                  <Palette className="h-4 w-4" />
                  Farben
                  {colorOverrideCount > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {colorOverrideCount}
                    </Badge>
                  )}
                </TabsTrigger>
                {hasPhotoLayers && useAiBackground && (
                  <TabsTrigger value="persons" className="gap-2" data-testid="persons-tab">
                    <User className="h-4 w-4" />
                    Personen
                    {selectedPersons.length > 0 && (
                      <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                        {selectedPersons.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                )}
              </TabsList>

              <ScrollArea className="h-[320px] mt-4">
                {/* Text Tab */}
                <TabsContent value="text" className="mt-0 pr-4">
                  {editableElements.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Edit3 className="h-10 w-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Keine editierbaren Textelemente im Template gefunden.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {editableElements.map(element => (
                        <div key={element.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor={element.id} className="text-sm font-medium capitalize">
                              {element.id.replace(/_/g, " ")}
                            </Label>
                            <Badge variant="outline" className="text-xs">
                              {element.type}
                            </Badge>
                          </div>
                          {element.type === "button" ? (
                            <Input
                              id={element.id}
                              placeholder={element.text}
                              value={textOverrides[element.id] || ""}
                              onChange={(e) => handleTextChange(element.id, e.target.value)}
                              data-testid={`text-input-${element.id}`}
                            />
                          ) : (
                            <Textarea
                              id={element.id}
                              placeholder={element.text}
                              value={textOverrides[element.id] || ""}
                              onChange={(e) => handleTextChange(element.id, e.target.value)}
                              className="min-h-[60px]"
                              data-testid={`text-input-${element.id}`}
                            />
                          )}
                          <p className="text-xs text-muted-foreground">
                            Original: "{element.text}"
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Colors Tab */}
                <TabsContent value="colors" className="mt-0 pr-4">
                  {!hasColors ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Palette className="h-10 w-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Keine Farben im Blueprint gefunden.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Background Colors */}
                      {extractedColors.background.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold flex items-center gap-2">
                            <Layers className="h-4 w-4" />
                            Hintergrund
                          </Label>
                          <div className="space-y-1 rounded-lg border p-2">
                            {extractedColors.background.map(color => (
                              <ColorSwatchEditor
                                key={color.id}
                                label={color.label}
                                colorHex={colorOverrides[color.id] || color.hex}
                                onChange={(newHex) => handleColorChange(color.id, newHex)}
                                description={color.description}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Typography Colors */}
                      {extractedColors.typography.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Typografie
                          </Label>
                          <div className="space-y-1 rounded-lg border p-2">
                            {extractedColors.typography.map(color => (
                              <ColorSwatchEditor
                                key={color.id}
                                label={color.label}
                                colorHex={colorOverrides[color.id] || color.hex}
                                onChange={(newHex) => handleColorChange(color.id, newHex)}
                                description={color.description}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Element Colors */}
                      {extractedColors.elements.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold flex items-center gap-2">
                            <LayoutTemplate className="h-4 w-4" />
                            Elemente
                          </Label>
                          <div className="space-y-1 rounded-lg border p-2">
                            {extractedColors.elements.map(color => (
                              <ColorSwatchEditor
                                key={color.id}
                                label={color.label}
                                colorHex={colorOverrides[color.id] || color.hex}
                                onChange={(newHex) => handleColorChange(color.id, newHex)}
                                description={color.description}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Reset colors button */}
                      {colorOverrideCount > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setColorOverrides({})}
                          className="w-full"
                          data-testid="reset-colors-btn"
                        >
                          <RefreshCw className="h-3 w-3 mr-2" />
                          Farben zurücksetzen
                        </Button>
                      )}
                    </div>
                  )}
                </TabsContent>
                
                {/* Persons Tab */}
                {hasPhotoLayers && useAiBackground && (
                  <TabsContent value="persons" className="mt-0 pr-4">
                    {loadingPersons ? (
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                        <p className="text-sm text-muted-foreground mt-2">Lade Personen...</p>
                      </div>
                    ) : persons.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <User className="h-10 w-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Keine Personen in der Bibliothek gefunden.</p>
                        <p className="text-xs mt-1">Füge Personen in der Personen-Bibliothek hinzu.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Wähle Personen aus, die im generierten Hintergrundbild erscheinen sollen:
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {persons.map(person => (
                            <div 
                              key={person.id}
                              onClick={() => handlePersonToggle(person.id)}
                              className={`
                                relative cursor-pointer rounded-lg border-2 p-3 transition-all
                                ${selectedPersons.includes(person.id) 
                                  ? 'border-primary bg-primary/5' 
                                  : 'border-border hover:border-muted-foreground/50'
                                }
                              `}
                              data-testid={`person-${person.id}`}
                            >
                              <div className="flex items-center gap-3">
                                {person.thumbnail_url ? (
                                  <img 
                                    src={person.thumbnail_url} 
                                    alt={person.name}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                    <User className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{person.name}</p>
                                  {person.role && (
                                    <p className="text-xs text-muted-foreground truncate">{person.role}</p>
                                  )}
                                </div>
                              </div>
                              {selectedPersons.includes(person.id) && (
                                <div className="absolute top-2 right-2">
                                  <CheckCircle2 className="h-4 w-4 text-primary" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {selectedPersons.length > 0 && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setSelectedPersons([])}
                            className="w-full"
                          >
                            <RefreshCw className="h-3 w-3 mr-2" />
                            Auswahl zurücksetzen
                          </Button>
                        )}
                      </div>
                    )}
                  </TabsContent>
                )}
              </ScrollArea>
            </Tabs>

            <Button 
              onClick={handleGenerate} 
              disabled={generating}
              className="w-full"
              data-testid="generate-image-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {useAiBackground && hasPhotoLayers ? "Generiere mit KI..." : "Generiere..."}
                </>
              ) : (
                <>
                  {useAiBackground && hasPhotoLayers ? (
                    <Sparkles className="h-4 w-4 mr-2" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  {useAiBackground && hasPhotoLayers ? "Mit KI generieren" : "Bild generieren"}
                </>
              )}
            </Button>
          </div>

          {/* Right side: Preview */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Vorschau</Label>
            <div className="border rounded-lg bg-muted/30 min-h-[400px] flex items-center justify-center overflow-hidden">
              {generating ? (
                <div className="text-center space-y-4">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
                  <p className="text-sm text-muted-foreground">
                    {useAiBackground && hasPhotoLayers ? "Generiere KI-Hintergrund..." : "Generiere Bild..."}
                  </p>
                </div>
              ) : generatedImage ? (
                <img
                  src={`data:image/png;base64,${generatedImage}`}
                  alt="Generiertes Bild"
                  className="max-w-full max-h-[400px] object-contain rounded shadow-lg"
                  data-testid="generated-image"
                />
              ) : (
                <div className="text-center space-y-4 p-8">
                  <ImageIcon className="h-16 w-16 mx-auto text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">
                    Klicke auf "Bild generieren", um eine Vorschau zu sehen
                  </p>
                </div>
              )}
            </div>

            {generatedImage && (
              <Button 
                onClick={handleDownload} 
                variant="outline" 
                className="w-full"
                data-testid="download-generated-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Als PNG herunterladen
              </Button>
            )}
          </div>
        </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// NEW: Quick Generate Dialog - Uses reference image principle like Comparison Ads
const QuickGenerateDialog = ({ template, open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    jobTitle: "",
    companyName: "",
    companyColor: "#4CAF50",
    headline: "",
    subheadline: "",
    ctaText: "Jetzt bewerben!",
    additionalInfo: ""
  });
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [variationCount, setVariationCount] = useState(2);
  const [exportFormat, setExportFormat] = useState("1080x1080");
  const [referenceImageBase64, setReferenceImageBase64] = useState(null);
  const [loadingReference, setLoadingReference] = useState(false);
  
  // Analysis states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Person handling states
  const [showPersonDialog, setShowPersonDialog] = useState(false);
  const [personMode, setPersonMode] = useState(null); // "library", "ai_generate", "keep_similar"
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [persons, setPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  
  // Safe Zones states
  const [useSafeZones, setUseSafeZones] = useState(false);
  const [safeZoneType, setSafeZoneType] = useState("instagram_story");
  const [safeZones, setSafeZones] = useState([]);
  
  // Logo states
  const [logoMode, setLogoMode] = useState(null); // "keep", "remove", "replace"
  const [selectedLogoId, setSelectedLogoId] = useState(null);
  const [logoPosition, setLogoPosition] = useState("same"); // "same" = same position as original, or specific position
  const [logos, setLogos] = useState([]);
  const [loadingLogos, setLoadingLogos] = useState(false);
  const [showLogoDialog, setShowLogoDialog] = useState(false);
  
  // Text editing states
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [detectedTexts, setDetectedTexts] = useState([]);
  const [selectedText, setSelectedText] = useState(null);
  const [replacementText, setReplacementText] = useState("");
  const [isDetecting, setIsDetecting] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);

  // Get current selected image
  const currentImage = generatedImages[selectedImageIndex]?.image || null;

  // Load safe zones and logos on mount
  useEffect(() => {
    const loadSafeZones = async () => {
      try {
        const response = await axios.get(`${API}/safe-zones`);
        setSafeZones(response.data.safe_zones || []);
      } catch (err) {
        console.error("Error loading safe zones:", err);
      }
    };
    const loadLogosForQuickGen = async () => {
      try {
        const response = await axios.get(`${API}/logos`);
        // API returns array directly, not {logos: [...]}
        const logosData = Array.isArray(response.data) ? response.data : (response.data.logos || []);
        setLogos(logosData);
      } catch (err) {
        console.error("Error loading logos:", err);
      }
    };
    loadSafeZones();
    loadLogosForQuickGen();
  }, []);

  // Load persons library
  const loadPersons = async () => {
    setLoadingPersons(true);
    try {
      const response = await axios.get(`${API}/persons`);
      // API returns array directly, not {persons: [...]}
      setPersons(Array.isArray(response.data) ? response.data : (response.data.persons || []));
    } catch (err) {
      console.error("Error loading persons:", err);
    } finally {
      setLoadingPersons(false);
    }
  };

  // Load logos library
  const loadLogos = async () => {
    setLoadingLogos(true);
    try {
      const response = await axios.get(`${API}/logos`);
      // API returns array directly, not {logos: [...]}
      const logosData = Array.isArray(response.data) ? response.data : (response.data.logos || []);
      setLogos(logosData);
    } catch (err) {
      console.error("Error loading logos:", err);
    } finally {
      setLoadingLogos(false);
    }
  };

  // Analyze reference image when loaded
  const analyzeReferenceImage = async (imageBase64) => {
    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/meta-ad-templates/analyze-reference`, {
        image_data: imageBase64
      });
      
      if (response.data.success && response.data.analysis) {
        const analysis = response.data.analysis;
        setAnalysisResult(analysis);
        
        // Pre-fill form fields
        setFormData(prev => ({
          ...prev,
          jobTitle: analysis.job_title || prev.jobTitle,
          companyName: analysis.company_name || prev.companyName,
          companyColor: analysis.dominant_color || prev.companyColor,
          headline: analysis.headline || prev.headline,
          subheadline: analysis.subheadline || prev.subheadline,
          ctaText: analysis.cta_text || prev.ctaText
        }));
        
        // Check if persons detected
        if (analysis.has_persons && analysis.person_count > 0) {
          setShowPersonDialog(true);
          loadPersons();
          toast.info(`${analysis.person_count} Person(en) im Bild erkannt!`);
        }
        
        toast.success("Referenzbild analysiert!");
      }
    } catch (err) {
      console.error("Error analyzing reference:", err);
      toast.error("Analyse fehlgeschlagen");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Load template image as reference when dialog opens
  useEffect(() => {
    const loadReferenceImage = async () => {
      if (open && template?.id && template.has_original_image) {
        setLoadingReference(true);
        setIsAnalyzing(true);
        try {
          const response = await axios.get(
            `${API}/meta-ad-templates/${template.id}/image`,
            { responseType: 'arraybuffer' }
          );
          const base64 = btoa(
            new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          setReferenceImageBase64(base64);
          
          // Automatically analyze the image - inline to avoid stale closure
          try {
            console.log("[ANALYZE] Starting reference image analysis...");
            const analysisResponse = await axios.post(`${API}/meta-ad-templates/analyze-reference`, {
              image_data: base64
            });
            
            if (analysisResponse.data.success && analysisResponse.data.analysis) {
              const analysis = analysisResponse.data.analysis;
              console.log("[ANALYZE] Analysis result:", analysis);
              console.log("[ANALYZE] Logo fields - has_logo:", analysis.has_logo, "logo_description:", analysis.logo_description, "logo_position:", analysis.logo_position);
              setAnalysisResult(analysis);
              
              // Pre-fill form fields
              setFormData(prev => ({
                ...prev,
                jobTitle: analysis.job_title || prev.jobTitle,
                companyName: analysis.company_name || prev.companyName,
                companyColor: analysis.dominant_color || prev.companyColor,
                headline: analysis.headline || prev.headline,
                subheadline: analysis.subheadline || prev.subheadline,
                ctaText: analysis.cta_text || prev.ctaText
              }));
              
              // Check if persons detected
              if (analysis.has_persons && analysis.person_count > 0) {
                setShowPersonDialog(true);
                // Load persons library
                try {
                  const personsResponse = await axios.get(`${API}/persons`);
                  // API returns array directly, not {persons: [...]}
                  setPersons(Array.isArray(personsResponse.data) ? personsResponse.data : (personsResponse.data.persons || []));
                } catch (e) {
                  console.error("Error loading persons:", e);
                }
                toast.info(`${analysis.person_count} Person(en) im Bild erkannt!`);
              }
              
              // Check if logo detected
              if (analysis.has_logo) {
                setShowLogoDialog(true);
                // Set initial logo position from analysis
                if (analysis.logo_position) {
                  setLogoPosition("same"); // Default to same position
                }
                // Load logos library for replacement option
                try {
                  const logosResponse = await axios.get(`${API}/logos`);
                  // API returns array directly, not {logos: [...]}
                  const logosData = Array.isArray(logosResponse.data) ? logosResponse.data : (logosResponse.data.logos || []);
                  setLogos(logosData);
                } catch (e) {
                  console.error("Error loading logos:", e);
                }
                toast.info("Logo im Bild erkannt!");
              }
              
              toast.success("Referenzbild analysiert!");
            } else {
              console.log("[ANALYZE] Analysis failed or no data:", analysisResponse.data);
            }
          } catch (analysisErr) {
            console.error("[ANALYZE] Error analyzing reference:", analysisErr);
            // Don't show error toast - just continue without auto-fill
          } finally {
            setIsAnalyzing(false);
          }
        } catch (err) {
          console.error("Error loading reference image:", err);
          toast.error("Referenzbild konnte nicht geladen werden");
          setIsAnalyzing(false);
        } finally {
          setLoadingReference(false);
        }
      }
    };
    loadReferenceImage();
  }, [open, template?.id, template?.has_original_image]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setGeneratedImages([]);
      setSelectedImageIndex(0);
      setReferenceImageBase64(null);
      setAnalysisResult(null);
      setShowPersonDialog(false);
      setPersonMode(null);
      setSelectedPersonId(null);
      // Reset logo states
      setShowLogoDialog(false);
      setLogoMode(null);
      setSelectedLogoId(null);
      setLogoPosition("same");
      setFormData({
        jobTitle: "",
        companyName: "",
        companyColor: "#4CAF50",
        headline: "",
        subheadline: "",
        ctaText: "Jetzt bewerben!",
        additionalInfo: ""
      });
    }
  }, [open]);

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerate = async () => {
    if (!formData.jobTitle.trim() || !formData.companyName.trim()) {
      toast.error("Bitte Berufsbezeichnung und Firmenname eingeben");
      return;
    }

    if (!referenceImageBase64) {
      toast.error("Referenzbild nicht geladen");
      return;
    }

    // Check if persons detected but no mode selected
    if (analysisResult?.has_persons && !personMode) {
      setShowPersonDialog(true);
      loadPersons();
      toast.info("Bitte wähle wie Personen behandelt werden sollen");
      return;
    }

    // Check if logo detected but no mode selected
    if (analysisResult?.has_logo && !logoMode) {
      setShowLogoDialog(true);
      loadLogos();
      toast.info("Bitte wähle wie das Logo behandelt werden soll");
      return;
    }

    setGenerating(true);
    setGeneratedImages([]);
    setSelectedImageIndex(0);
    setGenerationProgress({ current: 0, total: variationCount });

    // Create a job to track this generation - use /jobs/meta-ad for proper display in "Meine Aufträge"
    let jobId = null;
    try {
      const jobResponse = await axios.post(`${API}/jobs/meta-ad`, {
        template_id: template?.id,
        template_name: template?.name || formData.companyName || "Quick Generate",
        job_title: formData.jobTitle,
        company_name: formData.companyName,
        requested_count: variationCount
      }, {
        timeout: 60000 // 60 seconds for job creation
      });
      jobId = jobResponse.data.job_id;
      
      toast.info("Auftrag erstellt - Bilder werden in 'Meine Aufträge' gespeichert");
    } catch (err) {
      console.warn("Could not create job tracking:", err);
      toast.warning("Job-Tracking fehlgeschlagen - Bilder werden nur im Dialog angezeigt");
    }

    try {
      toast.info(`Generiere ${variationCount} Variationen... (ca. ${variationCount * 60}s)`);

      let failedCount = 0;
      
      for (let i = 0; i < variationCount; i++) {
        try {
          setGenerationProgress({ current: i, total: variationCount });
          
          const response = await axios.post(`${API}/meta-ad-templates/quick-generate`, {
            reference_image: referenceImageBase64,
            job_title: formData.jobTitle,
            company_name: formData.companyName,
            company_color: formData.companyColor,
            headline: formData.headline || null,
            subheadline: formData.subheadline || null,
            cta_text: formData.ctaText,
            additional_info: formData.additionalInfo || null,
            export_format: exportFormat,
            variation_index: i + 1,
            person_mode: personMode,
            selected_person_id: selectedPersonId,
            use_safe_zones: useSafeZones,
            safe_zone_type: useSafeZones ? safeZoneType : null,
            // Logo handling parameters
            logo_mode: logoMode,
            logo_id: logoMode === "replace" ? selectedLogoId : null,
            logo_position: logoMode === "replace" ? (logoPosition === "same" ? analysisResult?.logo_position : logoPosition) : null
          }, {
            timeout: 180000 // 3 minutes per image generation
          });

          if (response.data.success && response.data.image) {
            // Progressive update - add each image as it's generated
            setGeneratedImages(prev => [...prev, {
              image: response.data.image,
              index: i + 1
            }]);
            
            // Save image to job - use /jobs/meta-ad endpoint
            if (jobId) {
              try {
                await axios.post(`${API}/jobs/meta-ad/${jobId}/image`, {
                  image_data: response.data.image
                }, {
                  timeout: 60000
                });
              } catch (err) {
                console.warn("Could not save image to job:", err);
              }
            }
            
            toast.success(`Bild ${i + 1} fertig!`);
          } else {
            failedCount++;
            toast.warning(`Bild ${i + 1} fehlgeschlagen`);
          }
        } catch (error) {
          console.error(`Error generating variation ${i + 1}:`, error);
          failedCount++;
          if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            toast.error(`Bild ${i + 1} Timeout - Server überlastet`);
          } else {
            toast.error(`Bild ${i + 1} Fehler: ${error.response?.data?.detail || error.message}`);
          }
        }
      }

      // Update job status - use /jobs/meta-ad endpoint
      if (jobId) {
        try {
          const errorMsg = failedCount > 0 ? `${failedCount} von ${variationCount} Bildern fehlgeschlagen` : null;
          await axios.post(`${API}/jobs/meta-ad/${jobId}/complete`, {}, {
            params: { error_message: errorMsg },
            timeout: 30000
          });
        } catch (err) {
          console.warn("Could not update job status:", err);
        }
      }

      // Final summary
      const successCount = variationCount - failedCount;
      if (successCount > 0 && failedCount > 0) {
        toast.warning(`${successCount} von ${variationCount} Bildern generiert`);
      } else if (successCount > 0) {
        toast.success(`Alle ${successCount} Bilder erfolgreich generiert! Gespeichert unter "Meine Aufträge"`);
      } else {
        toast.error("Keine Bilder konnten generiert werden");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error("Fehler bei der Generierung");
      
      // Mark job as failed - use /jobs/meta-ad endpoint
      if (jobId) {
        try {
          await axios.post(`${API}/jobs/meta-ad/${jobId}/complete`, {}, {
            params: { error_message: error.message },
            timeout: 30000
          });
        } catch (err) {
          console.warn("Could not update job status:", err);
        }
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    const currentImage = generatedImages[selectedImageIndex]?.image;
    if (!currentImage) return;

    const link = document.createElement("a");
    link.href = `data:image/png;base64,${currentImage}`;
    link.download = `meta-ad-${formData.companyName}-${selectedImageIndex + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Bild heruntergeladen!");
  };

  // Detect text in generated image
  const detectText = async () => {
    if (!currentImage) return;
    
    setIsDetecting(true);
    try {
      const response = await axios.post(`${API}/editor/detect-text`, {
        image_data: currentImage
      });

      if (response.data.texts && response.data.texts.length > 0) {
        setDetectedTexts(response.data.texts);
        toast.success(`${response.data.texts.length} Text(e) erkannt`);
      } else {
        setDetectedTexts([]);
        toast.info("Kein Text im Bild erkannt");
      }
    } catch (error) {
      console.error("Text detection error:", error);
      toast.error("Fehler bei der Texterkennung");
    } finally {
      setIsDetecting(false);
    }
  };

  // Replace text using AI
  const replaceText = async () => {
    if (!selectedText || !replacementText.trim()) {
      toast.error("Bitte Text auswählen und neuen Text eingeben");
      return;
    }

    if (!currentImage) return;

    setIsReplacing(true);
    try {
      const response = await axios.post(`${API}/editor/replace-text`, {
        image_data: currentImage,
        original_text: selectedText.text,
        new_text: replacementText
      });

      if (response.data.success && response.data.image_data) {
        // Update the current image in the array
        setGeneratedImages(prev => {
          const updated = [...prev];
          updated[selectedImageIndex] = {
            ...updated[selectedImageIndex],
            image: response.data.image_data
          };
          return updated;
        });
        setSelectedText(null);
        setReplacementText("");
        setDetectedTexts([]);
        toast.success("Text erfolgreich ersetzt!");
      } else {
        toast.error(response.data.error || "KI konnte den Text nicht ersetzen");
      }
    } catch (error) {
      console.error("Text replacement error:", error);
      toast.error("Fehler bei der KI-Textbearbeitung");
    } finally {
      setIsReplacing(false);
    }
  };

  // Delete/Remove text from image (replace with empty/transparent)
  const deleteText = async () => {
    if (!selectedText) {
      toast.error("Bitte Text zum Löschen auswählen");
      return;
    }

    if (!currentImage) return;

    setIsReplacing(true);
    try {
      const response = await axios.post(`${API}/editor/replace-text`, {
        image_data: currentImage,
        original_text: selectedText.text,
        new_text: "" // Empty string to remove/paint over text
      });

      if (response.data.success && response.data.image_data) {
        // Update the current image in the array
        setGeneratedImages(prev => {
          const updated = [...prev];
          updated[selectedImageIndex] = {
            ...updated[selectedImageIndex],
            image: response.data.image_data
          };
          return updated;
        });
        setSelectedText(null);
        setReplacementText("");
        setDetectedTexts([]);
        toast.success("Text erfolgreich entfernt!");
      } else {
        toast.error(response.data.error || "KI konnte den Text nicht entfernen");
      }
    } catch (error) {
      console.error("Text deletion error:", error);
      toast.error("Fehler beim Entfernen des Textes");
    } finally {
      setIsReplacing(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Schnell-Generierung mit Referenzbild
          </DialogTitle>
          <DialogDescription>
            Erstelle ein neues Werbebild im Stil von "{template.name}" - schnell und einfach!
          </DialogDescription>
        </DialogHeader>

        {/* Person Selection Dialog */}
        {showPersonDialog && analysisResult?.has_persons && (
          <Card className="border-2 border-primary bg-primary/5 mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Personen erkannt ({analysisResult.person_count})
              </CardTitle>
              <CardDescription>
                {analysisResult.person_description || "Wie sollen die Personen im neuen Bild behandelt werden?"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Option 1: Use from library */}
              <div
                onClick={() => {
                  setPersonMode("library");
                  if (persons.length === 0) loadPersons();
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  personMode === "library" 
                    ? "border-primary bg-primary/10" 
                    : "border-muted hover:border-primary/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${personMode === "library" ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                  <span className="font-medium">Person aus Bibliothek verwenden</span>
                </div>
                <p className="text-xs text-muted-foreground ml-6 mt-1">Wähle eine Person aus deiner Personen-Bibliothek</p>
              </div>

              {/* Person Library Selection */}
              {personMode === "library" && (
                <div className="ml-6 space-y-2">
                  {loadingPersons ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Lade Personen...
                    </div>
                  ) : persons.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Keine Personen in der Bibliothek</p>
                  ) : (
                    <ScrollArea className="h-24">
                      <div className="flex gap-2 pb-2">
                        {persons.map((person) => (
                          <div
                            key={person.id}
                            onClick={() => setSelectedPersonId(person.id)}
                            className={`relative flex-shrink-0 cursor-pointer rounded-lg border-2 p-1 transition-all ${
                              selectedPersonId === person.id 
                                ? "border-primary bg-primary/10" 
                                : "border-transparent hover:border-muted"
                            }`}
                          >
                            <img
                              src={`${API}/persons/${person.id}/image?token=${localStorage.getItem('auth_token')}`}
                              alt={person.name}
                              className="w-16 h-16 object-cover rounded"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <p className="text-xs text-center truncate w-16 mt-1">{person.name}</p>
                            {selectedPersonId === person.id && (
                              <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                                <Check className="w-3 h-3" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              )}

              {/* Option 2: AI generates new person */}
              <div
                onClick={() => {
                  setPersonMode("ai_generate");
                  setSelectedPersonId(null);
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  personMode === "ai_generate" 
                    ? "border-primary bg-primary/10" 
                    : "border-muted hover:border-primary/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${personMode === "ai_generate" ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                  <span className="font-medium">KI soll neue Person generieren</span>
                </div>
                <p className="text-xs text-muted-foreground ml-6 mt-1">Die KI erstellt eine passende Person für den Beruf</p>
              </div>

              {/* Option 3: Keep similar */}
              <div
                onClick={() => {
                  setPersonMode("keep_similar");
                  setSelectedPersonId(null);
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  personMode === "keep_similar" 
                    ? "border-primary bg-primary/10" 
                    : "border-muted hover:border-primary/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${personMode === "keep_similar" ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                  <span className="font-medium">Ähnliche Person erstellen</span>
                </div>
                <p className="text-xs text-muted-foreground ml-6 mt-1">Die KI erstellt eine ähnliche Person wie im Original</p>
              </div>

              <Button 
                onClick={() => setShowPersonDialog(false)} 
                className="w-full mt-2"
                disabled={personMode === "library" && !selectedPersonId}
              >
                <Check className="w-4 h-4 mr-2" />
                Auswahl bestätigen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Logo Selection Dialog */}
        {showLogoDialog && analysisResult?.has_logo && (
          <Card className="border-2 border-amber-500 bg-amber-50/50 dark:bg-amber-950/20 mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-amber-600" />
                Logo erkannt
              </CardTitle>
              <CardDescription>
                {analysisResult.logo_description || "Ein Firmenlogo wurde im Bild gefunden."}
                {analysisResult.logo_position && (
                  <span className="block mt-1 text-xs">
                    Position: {
                      {
                        "top_left": "Oben links",
                        "top_right": "Oben rechts",
                        "top_center": "Oben mittig",
                        "bottom_left": "Unten links",
                        "bottom_right": "Unten rechts",
                        "bottom_center": "Unten mittig",
                        "center": "Mittig"
                      }[analysisResult.logo_position] || analysisResult.logo_position
                    }
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Option 1: Keep logo */}
              <div
                onClick={() => {
                  setLogoMode("keep");
                  setSelectedLogoId(null);
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  logoMode === "keep" 
                    ? "border-amber-500 bg-amber-100/50 dark:bg-amber-900/30" 
                    : "border-muted hover:border-amber-400"
                }`}
                data-testid="logo-keep-option"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${logoMode === "keep" ? "bg-amber-500 border-amber-500" : "border-muted-foreground"}`} />
                  <span className="font-medium">Logo beibehalten</span>
                </div>
                <p className="text-xs text-muted-foreground ml-6 mt-1">Das Original-Logo bleibt im Bild</p>
              </div>

              {/* Option 2: Remove logo */}
              <div
                onClick={() => {
                  setLogoMode("remove");
                  setSelectedLogoId(null);
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  logoMode === "remove" 
                    ? "border-amber-500 bg-amber-100/50 dark:bg-amber-900/30" 
                    : "border-muted hover:border-amber-400"
                }`}
                data-testid="logo-remove-option"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${logoMode === "remove" ? "bg-amber-500 border-amber-500" : "border-muted-foreground"}`} />
                  <span className="font-medium">Logo entfernen</span>
                </div>
                <p className="text-xs text-muted-foreground ml-6 mt-1">Das Logo wird aus dem Bild entfernt</p>
              </div>

              {/* Option 3: Replace logo */}
              <div
                onClick={() => {
                  setLogoMode("replace");
                  if (logos.length === 0) loadLogos();
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  logoMode === "replace" 
                    ? "border-amber-500 bg-amber-100/50 dark:bg-amber-900/30" 
                    : "border-muted hover:border-amber-400"
                }`}
                data-testid="logo-replace-option"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${logoMode === "replace" ? "bg-amber-500 border-amber-500" : "border-muted-foreground"}`} />
                  <span className="font-medium">Logo ersetzen</span>
                </div>
                <p className="text-xs text-muted-foreground ml-6 mt-1">Durch ein eigenes Logo aus der Bibliothek ersetzen</p>
              </div>

              {/* Logo Library Selection */}
              {logoMode === "replace" && (
                <div className="ml-6 space-y-3">
                  {/* Logo selection */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Logo auswählen:</Label>
                    {loadingLogos ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Lade Logos...
                      </div>
                    ) : logos.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Keine Logos in der Bibliothek. Bitte zuerst ein Logo hochladen.</p>
                    ) : (
                      <ScrollArea className="h-24">
                        <div className="flex gap-2 pb-2">
                          {logos.map((logo) => (
                            <div
                              key={logo.id}
                              onClick={() => setSelectedLogoId(logo.id)}
                              className={`relative flex-shrink-0 cursor-pointer rounded-lg border-2 p-1 transition-all ${
                                selectedLogoId === logo.id 
                                  ? "border-amber-500 bg-amber-100/50" 
                                  : "border-transparent hover:border-muted"
                              }`}
                              data-testid={`logo-select-${logo.id}`}
                            >
                              <img
                                src={`${API}/logos/${logo.id}/image?token=${localStorage.getItem('auth_token')}`}
                                alt={logo.name}
                                className="w-16 h-16 object-contain rounded bg-white"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <p className="text-xs text-center truncate w-16 mt-1">{logo.name}</p>
                              {selectedLogoId === logo.id && (
                                <div className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full p-0.5">
                                  <Check className="w-3 h-3" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </div>

                  {/* Position selection */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Position für neues Logo:</Label>
                    <Select value={logoPosition} onValueChange={setLogoPosition}>
                      <SelectTrigger className="w-full" data-testid="logo-position-select">
                        <SelectValue placeholder="Position wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="same">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3 w-3" />
                            Gleiche Position wie Original
                          </div>
                        </SelectItem>
                        <SelectItem value="top_left">Oben links</SelectItem>
                        <SelectItem value="top_right">Oben rechts</SelectItem>
                        <SelectItem value="top_center">Oben mittig</SelectItem>
                        <SelectItem value="bottom_left">Unten links</SelectItem>
                        <SelectItem value="bottom_right">Unten rechts</SelectItem>
                        <SelectItem value="bottom_center">Unten mittig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <Button 
                onClick={() => setShowLogoDialog(false)} 
                className="w-full mt-2"
                disabled={logoMode === "replace" && !selectedLogoId}
                data-testid="logo-confirm-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Auswahl bestätigen
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
          {/* Left: Form */}
          <div className="space-y-4">
            {/* Reference Image Preview */}
            <Card className="bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Referenzbild
                  {isAnalyzing && <Loader2 className="h-3 w-3 animate-spin ml-2" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingReference ? (
                  <Skeleton className="w-full h-32 rounded-lg" />
                ) : referenceImageBase64 ? (
                  <img
                    src={`data:image/png;base64,${referenceImageBase64}`}
                    alt="Reference"
                    className="w-full max-h-32 object-contain rounded-lg"
                  />
                ) : (
                  <div className="h-32 flex items-center justify-center text-muted-foreground">
                    <LayoutTemplate className="h-8 w-8" />
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Die KI orientiert sich am Stil dieses Bildes
                </p>
              </CardContent>
            </Card>

            {/* Basic Info */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="jobTitle">Berufsbezeichnung *</Label>
                <Input
                  id="jobTitle"
                  placeholder="z.B. Pflegefachkraft, Erzieher:in"
                  value={formData.jobTitle}
                  onChange={(e) => updateFormData("jobTitle", e.target.value)}
                  className="mt-1"
                  data-testid="quick-job-title"
                />
              </div>

              <div>
                <Label htmlFor="companyName">Firmenname *</Label>
                <Input
                  id="companyName"
                  placeholder="z.B. MedCare, KITA Luna"
                  value={formData.companyName}
                  onChange={(e) => updateFormData("companyName", e.target.value)}
                  className="mt-1"
                  data-testid="quick-company-name"
                />
              </div>

              <div>
                <Label htmlFor="companyColor">Firmenfarbe</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={formData.companyColor}
                    onChange={(e) => updateFormData("companyColor", e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border"
                  />
                  <Input
                    value={formData.companyColor}
                    onChange={(e) => updateFormData("companyColor", e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Optional Overrides */}
            <div className="space-y-3 pt-2 border-t">
              <p className="text-sm font-medium text-muted-foreground">Optionale Anpassungen</p>
              
              <div>
                <Label htmlFor="headline">Headline (optional)</Label>
                <Input
                  id="headline"
                  placeholder="Eigene Überschrift..."
                  value={formData.headline}
                  onChange={(e) => updateFormData("headline", e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="subheadline">Subheadline (optional)</Label>
                <Input
                  id="subheadline"
                  placeholder="Untertitel..."
                  value={formData.subheadline}
                  onChange={(e) => updateFormData("subheadline", e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="ctaText">CTA Button Text</Label>
                <Input
                  id="ctaText"
                  placeholder="Jetzt bewerben!"
                  value={formData.ctaText}
                  onChange={(e) => updateFormData("ctaText", e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="additionalInfo">Zusätzliche Infos für die KI</Label>
                <Textarea
                  id="additionalInfo"
                  placeholder="z.B. 'Zeige Gehalt €3500+', 'Dunkler Hintergrund', etc."
                  value={formData.additionalInfo}
                  onChange={(e) => updateFormData("additionalInfo", e.target.value)}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>

            {/* Generation Options */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label>Anzahl Variationen</Label>
                <Select value={String(variationCount)} onValueChange={(v) => setVariationCount(Number(v))}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label>Format</Label>
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1080x1080">Square (1080×1080)</SelectItem>
                    <SelectItem value="1080x1920">Story (1080×1920)</SelectItem>
                    <SelectItem value="1200x628">Banner (1200×628)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Safe Zones Section */}
              <div className="border rounded-lg p-3 space-y-3 bg-muted/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="safe-zones-toggle" className="cursor-pointer">
                      Safe Zones beachten
                    </Label>
                  </div>
                  <Switch
                    id="safe-zones-toggle"
                    checked={useSafeZones}
                    onCheckedChange={(checked) => {
                      setUseSafeZones(checked);
                      // Auto-switch to Story format when enabling safe zones
                      if (checked && exportFormat === "1080x1080") {
                        setExportFormat("1080x1920");
                        toast.info("Format auf Story (1080×1920) geändert für Safe Zones");
                      }
                    }}
                    data-testid="safe-zones-toggle"
                  />
                </div>
                
                {useSafeZones && (
                  <>
                    <Select 
                      value={safeZoneType} 
                      onValueChange={(value) => {
                        setSafeZoneType(value);
                        // Ensure Story format for all safe zone types
                        if (exportFormat !== "1080x1920") {
                          setExportFormat("1080x1920");
                          toast.info("Format auf Story (1080×1920) geändert - erforderlich für Safe Zones");
                        }
                      }}
                    >
                      <SelectTrigger className="w-full" data-testid="safe-zone-type-select">
                        <SelectValue placeholder="Plattform wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {safeZones.map((zone) => (
                          <SelectItem key={zone.id} value={zone.id}>
                            {zone.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Warning if format is not Story */}
                    {exportFormat !== "1080x1920" && (
                      <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded border border-amber-200">
                        <p className="font-medium">⚠️ Format-Warnung</p>
                        <p>Safe Zones erfordern Story-Format (1080×1920). Bitte Format ändern!</p>
                      </div>
                    )}
                    
                    {safeZones.find(z => z.id === safeZoneType) && (
                      <div className="text-xs text-muted-foreground bg-background p-2 rounded border">
                        <p className="font-medium mb-1">
                          {safeZones.find(z => z.id === safeZoneType)?.name}
                        </p>
                        <p>{safeZones.find(z => z.id === safeZoneType)?.description}</p>
                        <p className="mt-1">
                          Sicherer Bereich: {safeZones.find(z => z.id === safeZoneType)?.safe_width}×
                          {safeZones.find(z => z.id === safeZoneType)?.safe_height}px
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || !formData.jobTitle.trim() || !formData.companyName.trim() || loadingReference}
              className="w-full"
              size="lg"
              data-testid="quick-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generiere...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {variationCount} Bild{variationCount > 1 ? "er" : ""} generieren
                </>
              )}
            </Button>
          </div>

          {/* Right: Generated Images */}
          <div className="space-y-4">
            {/* Progress Bar during generation */}
            {generating && generationProgress.total > 0 && (
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Generiere Bilder...</span>
                  <span className="text-muted-foreground">
                    {generatedImages.length} von {generationProgress.total} fertig
                  </span>
                </div>
                <Progress value={(generatedImages.length / generationProgress.total) * 100} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  Bild {generatedImages.length + 1} wird generiert (~30 Sekunden)
                </p>
              </div>
            )}
            
            {generatedImages.length > 0 ? (
              <>
                {/* Main Image */}
                <div className="relative bg-muted/30 rounded-lg overflow-hidden">
                  <img
                    src={`data:image/png;base64,${generatedImages[selectedImageIndex]?.image}`}
                    alt={`Generated ${selectedImageIndex + 1}`}
                    className="w-full rounded-lg"
                  />
                  <Badge className="absolute top-2 left-2">
                    Variation {selectedImageIndex + 1}
                  </Badge>
                </div>

                {/* Thumbnails */}
                {generatedImages.length > 1 && (
                  <div className="grid grid-cols-3 gap-2">
                    {generatedImages.map((img, idx) => (
                      <div
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                          selectedImageIndex === idx
                            ? "border-primary ring-2 ring-primary/30"
                            : "border-transparent hover:border-muted-foreground/50"
                        }`}
                      >
                        <img
                          src={`data:image/png;base64,${img.image}`}
                          alt={`Variation ${idx + 1}`}
                          className="w-full aspect-square object-cover"
                        />
                        {selectedImageIndex === idx && (
                          <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                            <CheckCircle2 className="w-6 h-6 text-primary" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button onClick={handleDownload} variant="outline" className="flex-1">
                    <Download className="h-4 w-4 mr-2" />
                    Herunterladen
                  </Button>
                  <Button 
                    onClick={() => setShowTextEditor(true)} 
                    variant="secondary" 
                    className="flex-1"
                    data-testid="edit-text-btn"
                  >
                    <Type className="h-4 w-4 mr-2" />
                    Text bearbeiten
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-muted-foreground">
                <Wand2 className="h-16 w-16 mb-4 opacity-30" />
                <p className="text-sm">Gib deine Daten ein und klicke auf "Generieren"</p>
                <p className="text-xs mt-1">Die KI erstellt ein Bild im Stil des Referenzbildes</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Text Editor Dialog */}
      <Dialog open={showTextEditor} onOpenChange={setShowTextEditor}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Type className="h-5 w-5" />
              Text im Bild bearbeiten
            </DialogTitle>
            <DialogDescription>
              Mit KI-Power kannst du Texte direkt im Bild ändern oder entfernen
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Image Preview */}
            {currentImage && (
              <div className="relative rounded-lg overflow-hidden border">
                <img
                  src={`data:image/png;base64,${currentImage}`}
                  alt="Current"
                  className="w-full max-h-48 object-contain"
                />
              </div>
            )}

            {/* Detect Text Button */}
            <Button
              onClick={detectText}
              disabled={isDetecting || !currentImage}
              className="w-full"
              variant="secondary"
              data-testid="detect-text-btn"
            >
              {isDetecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Erkenne Texte...
                </>
              ) : (
                <>
                  <ScanSearch className="w-4 h-4 mr-2" />
                  Texte im Bild erkennen
                </>
              )}
            </Button>

            {/* Detected Texts */}
            {detectedTexts.length > 0 && (
              <div className="space-y-2">
                <Label>Erkannte Texte (klicke zum Auswählen):</Label>
                <ScrollArea className="h-40 border rounded-lg p-2">
                  {detectedTexts.map((textItem, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded cursor-pointer hover:bg-muted mb-1 transition-colors ${
                        selectedText?.text === textItem.text ? "bg-primary/10 border border-primary" : ""
                      }`}
                      onClick={() => {
                        setSelectedText(textItem);
                        setReplacementText(textItem.text);
                      }}
                    >
                      <p className="text-sm font-medium truncate">{textItem.text}</p>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}

            {/* Replace/Delete Text Form */}
            {selectedText && (
              <div className="space-y-3 border-t pt-4">
                <div>
                  <Label>Ausgewählter Text:</Label>
                  <p className="text-sm bg-muted p-2 rounded mt-1 font-mono">{selectedText.text}</p>
                </div>
                <div>
                  <Label htmlFor="newText">Neuer Text (leer lassen zum Löschen):</Label>
                  <Input
                    id="newText"
                    value={replacementText}
                    onChange={(e) => setReplacementText(e.target.value)}
                    placeholder="Neuen Text eingeben oder leer lassen..."
                    className="mt-1"
                    data-testid="replacement-text-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={replaceText}
                    disabled={isReplacing || !replacementText.trim()}
                    className="flex-1"
                    data-testid="replace-text-btn"
                  >
                    {isReplacing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Ersetze...
                      </>
                    ) : (
                      <>
                        <Type className="w-4 h-4 mr-2" />
                        Text ersetzen
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={deleteText}
                    disabled={isReplacing}
                    variant="destructive"
                    className="flex-1"
                    data-testid="delete-text-btn"
                  >
                    {isReplacing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Entferne...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Text löschen
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowTextEditor(false);
              setDetectedTexts([]);
              setSelectedText(null);
              setReplacementText("");
            }}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// Main component
export const MetaAdTemplatesLibrary = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showQuickGenerateDialog, setShowQuickGenerateDialog] = useState(false); // NEW
  const [generateTarget, setGenerateTarget] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [viewMode, setViewMode] = useState("grid");

  const fetchTemplates = useCallback(async (silent = false) => {
    try {
      const response = await axios.get(`${API}/meta-ad-templates`, {
        timeout: 60000 // 60 seconds for template fetch
      });
      setTemplates(response.data || []);
    } catch (err) {
      console.error("Error fetching templates:", err);
      // Don't show error toast or clear templates on timeout - just keep existing data
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        console.warn("Template fetch timeout - keeping existing data");
        return; // Keep existing templates, don't clear
      }
      if (!silent && err.response?.status !== 403 && err.response?.status !== 404) {
        toast.error("Fehler beim Laden der Templates");
      }
      // Only clear templates on actual errors, not timeouts
      if (err.response?.status === 403 || err.response?.status === 404) {
        setTemplates([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    
    // Poll for updates (for analyzing templates) - silent mode
    const interval = setInterval(() => fetchTemplates(true), 10000); // 10 seconds, silent
    return () => clearInterval(interval);
  }, [fetchTemplates]);

  const handleUpload = async (name, imageData) => {
    try {
      toast.info("Bild wird hochgeladen und analysiert...", {
        description: "Dies kann bis zu 2 Minuten dauern."
      });
      
      const response = await axios.post(`${API}/meta-ad-templates/analyze`, {
        name,
        image_data: imageData
      }, {
        timeout: 180000 // 3 minutes for upload + analysis
      });
      
      toast.success("Template erstellt!", {
        description: "Die KI-Analyse läuft im Hintergrund."
      });
      
      // Add to list immediately
      setTemplates(prev => [response.data, ...prev]);
      
    } catch (err) {
      console.error("Upload error:", err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.warning("Analyse dauert länger als erwartet", {
          description: "Das Template wird im Hintergrund analysiert. Bitte aktualisieren Sie die Seite in 1-2 Minuten."
        });
      } else {
        toast.error("Fehler beim Hochladen", {
          description: err.response?.data?.detail || "Bitte versuche es erneut."
        });
        throw err;
      }
    }
  };

  const handleDelete = async (template) => {
    try {
      await axios.delete(`${API}/meta-ad-templates/${template.id}`, {
        timeout: 60000
      });
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      toast.success("Template gelöscht");
    } catch (err) {
      console.error("Delete error:", err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.warning("Löschen dauert länger - bitte Seite aktualisieren");
      } else {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleView = (template) => {
    setSelectedTemplate(template);
    setShowDetailDialog(true);
  };

  const handleShare = (template) => {
    setShareTarget(template);
    setShareDialogOpen(true);
  };

  const handleDuplicate = async (template) => {
    // For now, just show a toast - full implementation would create a copy
    toast.info("Duplizieren", {
      description: "Diese Funktion wird in Kürze verfügbar sein."
    });
  };

  const handleGenerate = (template) => {
    // Only allow generation for completed templates with original image
    if (template.status !== "completed") {
      toast.error("Template nicht bereit", {
        description: "Nur abgeschlossene Templates können verwendet werden."
      });
      return;
    }
    if (!template.has_original_image) {
      toast.error("Kein Referenzbild vorhanden", {
        description: "Dieses Template hat kein Originalbild."
      });
      return;
    }
    setGenerateTarget(template);
    setShowQuickGenerateDialog(true); // Use new quick generate dialog
  };

  // Filter templates
  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Count by status
  const analyzingCount = templates.filter(t => t.status === "analyzing").length;
  const completedCount = templates.filter(t => t.status === "completed").length;

  return (
    <div className="space-y-6" data-testid="meta-ad-templates-library">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <LayoutTemplate className="h-6 w-6 text-primary" />
            Meta Ad Templates
          </h1>
          <p className="text-muted-foreground mt-1">
            Analysiere erfolgreiche Werbeanzeigen und erstelle wiederverwendbare Templates
          </p>
        </div>
        <Button 
          onClick={() => setShowUploadDialog(true)}
          data-testid="create-template-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Neues Template
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-2xl font-bold">{templates.length}</div>
          <div className="text-sm text-muted-foreground">Gesamt</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-green-600">{completedCount}</div>
          <div className="text-sm text-muted-foreground">Bereit</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-blue-600">{analyzingCount}</div>
          <div className="text-sm text-muted-foreground">In Analyse</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-orange-600">
            {templates.filter(t => t.status === "failed").length}
          </div>
          <div className="text-sm text-muted-foreground">Fehlgeschlagen</div>
        </Card>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Templates durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-templates-input"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("grid")}
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={fetchTemplates}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Templates grid/list */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="aspect-square" />
              <div className="p-4 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </Card>
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card className="p-12 text-center">
          <LayoutTemplate className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            {searchQuery ? "Keine Templates gefunden" : "Noch keine Templates"}
          </h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery 
              ? "Versuche einen anderen Suchbegriff"
              : "Lade ein Werbebild hoch, um dein erstes Template zu erstellen"
            }
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowUploadDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Template erstellen
            </Button>
          )}
        </Card>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onView={handleView}
              onDelete={handleDelete}
              onShare={handleShare}
              onDuplicate={handleDuplicate}
              onGenerate={handleGenerate}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTemplates.map(template => (
            <Card 
              key={template.id} 
              className="p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => handleView(template)}
            >
              <div className="w-16 h-16 rounded bg-muted flex items-center justify-center overflow-hidden">
                {template.has_original_image && template.status === "completed" ? (
                  <AuthenticatedImage
                    src={`${API}/meta-ad-templates/${template.id}/image`}
                    alt={template.name}
                    className="w-full h-full object-cover"
                    fallback={<LayoutTemplate className="h-6 w-6 text-muted-foreground" />}
                  />
                ) : (
                  <LayoutTemplate className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium truncate">{template.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {new Date(template.created_at).toLocaleDateString("de-DE")}
                  {template.blueprint?.elements && ` • ${template.blueprint.elements.length} Elemente`}
                </p>
              </div>
              <StatusBadge status={template.status} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button size="icon" variant="ghost">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleShare(template); }}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Teilen
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); handleDelete(template); }}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Löschen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <UploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onUpload={handleUpload}
      />

      {/* Detail Dialog */}
      <TemplateDetailDialog
        template={selectedTemplate}
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
      />

      {/* Share Dialog */}
      {shareTarget && (
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          itemId={shareTarget.id}
          itemType="meta-ad-templates"
          itemName={shareTarget.name}
        />
      )}

      {/* Generate Dialog */}
      {generateTarget && (
        <GenerateDialog
          template={generateTarget}
          open={showGenerateDialog}
          onOpenChange={setShowGenerateDialog}
        />
      )}

      {/* Quick Generate Dialog (NEW - Reference Image Based) */}
      {generateTarget && (
        <QuickGenerateDialog
          template={generateTarget}
          open={showQuickGenerateDialog}
          onOpenChange={setShowQuickGenerateDialog}
        />
      )}
    </div>
  );
};

export default MetaAdTemplatesLibrary;
